import { useEffect, useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import type {
  ApprovalSummary,
  ApprovalDecision,
  ApprovalRisk,
} from '../api/types';
import { DiffViewer } from '../components/DiffViewer';
import { subscribeApprovals } from '../stores/ws';
import { useProjectScopedEffect } from '../stores/project';
// #114 (round-6 close-out) — swap the plain textarea for a
// server-relayed CRDT textarea so two reviewers see each other type.
import { CollaborativeTextarea } from '../components/CollaborativeTextarea';

/**
 * Approval Panel (FEAT-U07 / FEAT-ADD-017) + K1.2/K3.2 round-5.
 *
 * Pending-approvals list with per-item decision controls and an inline
 * diff viewer for the first artifact on each approval. Subscribes to
 * the /ws/approvals server channel so the queue updates when another
 * reviewer decides an item.
 */
export function ApprovalsRoute(): JSX.Element {
  const [approvals, setApprovals] = useState<ApprovalSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // N5.5 / R5 (round-6) — project-scoped; aborts in-flight on switch.
  useProjectScopedEffect((signal) => {
    setLoading(true);
    shaktiApi
      .listApprovals({ status: 'pending', signal })
      .then(setApprovals)
      .catch((e: unknown) => {
        if (signal.aborted) return;
        setError(e instanceof ApiError ? e.body?.error ?? e.message : 'Failed to load approvals');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  });

  const active = activeId ? approvals.find((a) => a.approval_id === activeId) ?? null : null;

  // K3.2 (round-5) — realtime subscription. When another reviewer
  // decides an approval, surface a toast and remove it from the list.
  useEffect(() => {
    const unsub = subscribeApprovals((event) => {
      if (event.type === 'approval.decided') {
        setApprovals((prev) => prev.filter((a) => a.approval_id !== event.approval_id));
        if (activeId === event.approval_id) {
          setActiveId(null);
        }
        setToast(`Approval ${event.approval_id.slice(0, 8)} was ${event.decision} by another reviewer.`);
        setTimeout(() => setToast(null), 4000);
      } else if (event.type === 'approval.new') {
        setApprovals((prev) => {
          if (prev.some((a) => a.approval_id === event.approval.approval_id)) return prev;
          return [event.approval, ...prev];
        });
      }
    });
    return unsub;
  }, [activeId]);

  // K1.2 — fetch content for the first artifact on the active approval
  // so DiffViewer can render. Uses getArtifactContent; the "old" side
  // stays empty-string since we don't currently track prior versions
  // server-side (v2.1 follow-up).
  const [activeArtifact, setActiveArtifact] = useState<string>('');
  useEffect(() => {
    if (!active || active.artifact_ids.length === 0) {
      setActiveArtifact('');
      return;
    }
    const firstId = active.artifact_ids[0];
    if (!firstId) {
      setActiveArtifact('');
      return;
    }
    const ac = new AbortController();
    shaktiApi
      .getArtifactContent(firstId, { signal: ac.signal })
      .then((c) => setActiveArtifact(typeof c === 'string' ? c : JSON.stringify(c, null, 2)))
      .catch(() => setActiveArtifact(''));
    return () => ac.abort();
  }, [active?.approval_id, active?.artifact_ids]);

  const onSubmit = async (decision: ApprovalDecision): Promise<void> => {
    if (!active) return;
    if (decision !== 'approve' && comment.trim().length < 3) {
      setError('A comment is required for reject / request_changes.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await shaktiApi.submitApproval(active.approval_id, {
        decision,
        comment: comment.trim(),
      });
      setApprovals((prev) => prev.filter((a) => a.approval_id !== active.approval_id));
      setActiveId(null);
      setComment('');
      setToast(`Decision "${decision}" recorded.`);
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  // N5.6 (round-6) — keyboard accelerators:
  //   Cmd/Ctrl-Enter            → approve the active item
  //   Cmd/Ctrl-Shift-Enter      → reject the active item
  // Only fire when an approval is active and not already submitting.
  useEffect(() => {
    if (!active || submitting) return;
    const handler = (e: KeyboardEvent) => {
      const accel = e.metaKey || e.ctrlKey;
      if (!accel || e.key !== 'Enter') return;
      e.preventDefault();
      if (e.shiftKey) {
        void onSubmit('reject');
      } else {
        void onSubmit('approve');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.approval_id, submitting, comment]);

  return (
    <section aria-labelledby="app-title" className="mx-auto flex max-w-5xl flex-col gap-4">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/approvals
        </span>
        <h1 id="app-title" className="text-2xl font-semibold text-fg-primary">
          Approval Queue
        </h1>
        <p className="text-sm text-fg-secondary">
          {loading ? 'Loading…' : `${approvals.length} pending approvals`}
        </p>
      </header>

      {toast ? (
        <div
          role="status"
          aria-live="polite"
          className="shakti-card border border-status-success/40 bg-status-success/10 text-sm text-status-success"
        >
          {toast}
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="shakti-card border border-status-error/40 text-status-error">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1.3fr]">
        <ul className="shakti-card flex flex-col gap-1" aria-label="Pending approvals">
          {approvals.length === 0 && !loading ? (
            <li className="text-sm text-fg-muted">No pending approvals.</li>
          ) : (
            approvals.map((a) => (
              <li key={a.approval_id}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveId(a.approval_id);
                    setComment('');
                    setError(null);
                  }}
                  className={`w-full rounded-sm border px-3 py-2 text-left text-sm transition-colors ${
                    activeId === a.approval_id
                      ? 'border-accent bg-accent-muted'
                      : 'border-border-subtle hover:border-border-strong'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-fg-primary">{a.title}</span>
                    <RiskBadge risk={a.risk} />
                  </div>
                  <div className="mt-1 text-2xs text-fg-muted">
                    <span className="shakti-mono">{a.phase}</span> · {a.artifact_ids.length}{' '}
                    artifacts · {new Date(a.created_at).toLocaleString()}
                  </div>
                </button>
              </li>
            ))
          )}
        </ul>

        <aside className="shakti-card flex flex-col gap-3" aria-label="Approval detail">
          <h2 className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            Decision
          </h2>
          {active ? (
            <>
              <dl className="flex flex-col gap-1 text-xs">
                <div>
                  <dt className="text-fg-muted">Approval ID</dt>
                  <dd className="shakti-mono">{active.approval_id}</dd>
                </div>
                <div>
                  <dt className="text-fg-muted">Phase</dt>
                  <dd className="shakti-mono">{active.phase}</dd>
                </div>
                <div>
                  <dt className="text-fg-muted">Artifacts</dt>
                  <dd className="shakti-mono text-2xs">
                    {active.artifact_ids.slice(0, 4).join(', ')}
                    {active.artifact_ids.length > 4 ? '…' : ''}
                  </dd>
                </div>
              </dl>
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-fg-muted">
                  Comment (required for non-approve) — collaborative
                </span>
                <CollaborativeTextarea
                  approvalId={active.approval_id}
                  initial={comment}
                  wsUrl={collaborativeWsUrl()}
                  onChange={setComment}
                  rows={4}
                  ariaLabel="Approval rationale (collaborative)"
                />
              </label>
              <div className="flex gap-2">
                <ActionButton
                  label="Approve"
                  tone="success"
                  disabled={submitting}
                  onClick={() => onSubmit('approve')}
                />
                <ActionButton
                  label="Request changes"
                  tone="warning"
                  disabled={submitting || comment.trim().length < 3}
                  onClick={() => onSubmit('request_changes')}
                />
                <ActionButton
                  label="Reject"
                  tone="danger"
                  disabled={submitting || comment.trim().length < 3}
                  onClick={() => onSubmit('reject')}
                />
              </div>
              {activeArtifact ? (
                <details className="mt-4" data-testid="approval-diff">
                  <summary className="cursor-pointer text-xs text-fg-muted">
                    Preview artifact ({active.artifact_ids[0]?.slice(0, 8) ?? ''})
                  </summary>
                  <div className="mt-2">
                    <DiffViewer
                      oldText=""
                      newText={activeArtifact}
                      oldTitle="Baseline"
                      newTitle="Proposed"
                      splitView={false}
                      monaco
                      filename={active.artifact_paths?.[0]}
                    />
                  </div>
                </details>
              ) : null}
            </>
          ) : (
            <p className="text-xs text-fg-muted">Select an approval to act on.</p>
          )}
        </aside>
      </div>
    </section>
  );
}

function RiskBadge({ risk }: { risk: ApprovalRisk }): JSX.Element {
  const tone = {
    low: 'bg-status-success/15 text-status-success',
    medium: 'bg-status-info/15 text-status-info',
    high: 'bg-status-warning/15 text-status-warning',
    critical: 'bg-status-error/15 text-status-error',
  }[risk];
  return (
    <span className={`rounded-sm px-2 py-0.5 text-2xs font-medium uppercase ${tone}`}>
      {risk}
    </span>
  );
}

function ActionButton({
  label,
  tone,
  onClick,
  disabled,
}: {
  label: string;
  tone: 'success' | 'warning' | 'danger';
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  const toneClass = {
    success: 'border-status-success/40 text-status-success hover:bg-status-success/10',
    warning: 'border-status-warning/40 text-status-warning hover:bg-status-warning/10',
    danger: 'border-status-error/40 text-status-error hover:bg-status-error/10',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-sm border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-40 ${toneClass}`}
    >
      {label}
    </button>
  );
}

/**
 * #114 (round-6 close-out) — resolve the CRDT relay WS URL so the
 * `CollaborativeTextarea` can connect to `/ws/crdt/approvals`
 * regardless of the dashboard origin.
 */
function collaborativeWsUrl(): string {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/crdt/approvals`;
}
